import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import {
  Modal as AntModal,
  Button,
  Input,
  Form,
  notification,
  Row,
  Space,
  Typography
} from 'antd'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import * as ROUTES from 'admin/routes'
import { useApi } from 'admin/lib/Api'
import { isoDateFromFormDate, isoDateToFormDate } from 'admin/lib/dates'
import { FormItem, SectionProvider, Section } from 'admin/lib/Forms'
import { Guid } from 'admin/lib/guid'
import { BodyContentColumn } from 'admin/lib/Layouts'
import {
  CheckCertificationNumber,
  CheckCertificationScheme,
  CheckCompanyName,
  CheckCertificationResourceURI,
  CheckDateIsInThePast,
  CheckExpiryDateAfterValidFromDate,
  CheckProductType
} from 'admin/lib/SimpleDataValidation'

export function EditCertificate({ certificate, isNew }) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  //
  // Form setup & values

  const form = useForm({
    defaultValues: blankCertificate,
    mode: 'all',
    criteriaMode: 'firstError'
  })
  const { reset } = form

  const draftItem = isNew ? blankCertificate : certificate

  useEffect(() => {
    reset(
      produce(draftItem, draft => {
        draft.validFromDate = isoDateToFormDate(draft.validFromDate)
        draft.expirationDate = isoDateToFormDate(draft.expirationDate)
      })
    )
  }, [draftItem, reset])

  //
  // Callbacks

  const api = useApi()

  const navigateToList = useCallback(() => {
    navigate(ROUTES.CERTIFICATES.LIST)
  }, [navigate])

  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)

      const submissionData = produce(draftItem, draft => {
        draft.certificateNumber = data.certificateNumber
        draft.validFromDate = isoDateFromFormDate(data.validFromDate)
        draft.expirationDate = isoDateFromFormDate(data.expirationDate)
        draft.resourceURI = data.resourceURI
        draft.certificationScheme = data.certificationScheme
        draft.certificationRating = data.certificationRating
        draft.productType = data.productType
        draft.companyName = data.companyName
      })

      const [, status, error] = isNew
        ? await api.registerCertificate(submissionData)
        : await api.updateCertificate(submissionData)

      if (error) {
        AntModal.error({
          title: 'Error Saving Certificate',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        setSubmitting(false)
        navigate(ROUTES.CERTIFICATES.LIST)
        notification.success({
          message: 'Successfully saved certificate'
        })
      }
    },
    [api, draftItem, isNew, navigate]
  )

  return (
    <FormProvider {...form}>
      <SectionProvider>
        <BodyContentColumn>
          <Form layout="vertical" onFinish={form.handleSubmit(onSubmit)}>
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'New Certificate' : 'Edit Certificate'}
                  </Typography.Title>
                  <FormItem
                    label="Certificate Number [e.g. TT-COC-005338]"
                    name="certificateNumber"
                    inputRef={firstInputRef}
                    rules={{
                      minLength: 2,
                      required: true
                    }}
                    simpleDataValidationRules={CheckCertificationNumber}
                  ></FormItem>

                  <FormItem
                    label="Certification Scheme [e.g. FSC]"
                    name="certificationScheme"
                    simpleDataValidationRules={CheckCertificationScheme}
                  ></FormItem>

                  <FormItem
                    label="Certification Rating [e.g. Mix]"
                    name="certificationRating"
                    simpleDataValidationRules={CheckCertificationScheme}
                  ></FormItem>

                  <FormItem
                    label="Product Type [e.g. W11.1 Doors and door frames]"
                    name="productType"
                    simpleDataValidationRules={CheckProductType}
                  ></FormItem>

                  <FormItem
                    label="Company Name [e.g. Lomax + Wood Limited]"
                    name="companyName"
                    simpleDataValidationRules={CheckCompanyName}
                  ></FormItem>

                  <FormItem
                    label="Valid From"
                    name="validFromDate"
                    simpleDataValidationRules={CheckDateIsInThePast}
                  >
                    <Input type="datetime-local" max="9999-12-31T23:59" />
                  </FormItem>

                  <FormItem
                    label="Expires At"
                    name="expirationDate"
                    simpleDataValidationRules={v =>
                      CheckExpiryDateAfterValidFromDate(
                        v,
                        form.control.getValues('validFromDate')
                      )
                    }
                  >
                    <Input type="datetime-local" max="9999-12-31T23:59" />
                  </FormItem>

                  <FormItem
                    label="Resource URI [e.g. https://info.fsc.org/details.php?id=a023300000VmOFlAAN&type=certificate]"
                    name="resourceURI"
                    id="resourceURI"
                    simpleDataValidationRules={CheckCertificationResourceURI}
                  ></FormItem>
                </>
              )}
            </Section>
            <Row justify="center">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={submitting}
                  style={{ width: '8rem' }}
                >
                  {isNew ? 'Create' : 'Update'}
                </Button>

                <Button onClick={navigateToList} style={{ width: '8rem' }}>
                  Cancel
                </Button>
              </Space>
            </Row>
          </Form>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

const blankCertificate = {
  certificateId: Guid.Empty,
  certificateNumber: '',
  eTag: '',
  createdAtDate: '0001-01-01T00:00:00Z',
  validFromDate: '0001-01-01T00:00:00Z',
  expirationDate: '0001-01-01T00:00:00Z',
  resourceURI: '',
  certificationScheme: '',
  certificationRating: '',
  productType: '',
  companyName: ''
}

EditCertificate.propTypes = {
  certificate: PropTypes.object,
  isNew: PropTypes.bool.isRequired
}
